/**
 * Lustro słownika `public.uom` i normalizator kodu jednostki — odpowiednik
 * funkcji SQL `public.uom_resolve()` z migracji 300.
 *
 * =============================================================================
 * DLACZEGO TO ISTNIEJE OSOBNO OD `unit.ts` — i dlaczego NIE WOLNO tego scalić
 * =============================================================================
 *
 * `unit.ts` ma już `resolveUnit()` (linia 418) razem z tablicą
 * `UNIT_CODE_ALIASES`. Ta funkcja celuje w INNY REJESTR: jej wynikiem są
 * identyfikatory z `STANDARD_UNITS` — `'liters'`, `'minutes'`, `'ton'`,
 * `'fahrenheit'`, `'celsius'`, `'hours'`, `'days'`. Zmierzone na produkcji
 * 2026-07-28 (`select code from public.uom`, 77 wierszy po migracji 300):
 * ŻADEN z kodów `liters`, `ton`, `minutes`, `hours`, `days`, `fahrenheit`
 * w `uom` NIE ISTNIEJE — potwierdzone wywołaniem `public.uom_resolve()` na
 * każdym z nich, wszystkie zwracają NULL.
 *
 * Skutek jest praktyczny, nie estetyczny: wpięcie `resolveUnit()` przed zapisem
 * do kolumny objętej kluczem obcym z migracji 295 (`fabrica_recipes.yield_unit`,
 * `fabrica_recipe_ingredients.unit`, `fabrica_quality_results.unit`, …)
 * zamieniłoby jedno naruszenie 23503 na drugie — tyle że trudniejsze do
 * zauważenia, bo poprzedzone „normalizacją", która wygląda na zabezpieczenie.
 *
 * Dlatego `resolveUomCode()` jest FUNKCJĄ NOWĄ, a semantyka `resolveUnit()`
 * zostaje nietknięta dla swoich dotychczasowych wołających (konwersje
 * `convertByCode` / `convertUnit`, które liczą liczby, a nie wybierają kod do
 * zapisu w bazie).
 *
 *   `resolveUnit(code)`     -> wiersz `Unit` z `STANDARD_UNITS`  (do LICZENIA)
 *   `resolveUomCode(input)` -> `string` będący `uom.code` | null (do ZAPISU)
 *
 * =============================================================================
 * DLACZEGO LUSTRO SŁOWNIKA, A NIE ZAPYTANIE DO BAZY
 * =============================================================================
 *
 * Odmowa musi paść ZANIM ładunek pojedzie do PostgREST — inaczej jedyną
 * odpowiedzią, jaką dostaje autor receptury, jest surowe 23503 „violates
 * foreign key constraint fk_fabrica_recipes_yield_uom", które nie mówi ANI
 * która wartość jest zła, ANI w którym miejscu formularza. Kontrakty ładunku
 * w `services/fabrica-core/src/api/` są celowo funkcjami CZYSTYMI (w tym repo
 * nie ma ani jednego `vi.mock`), więc zapytanie do bazy odebrałoby im
 * testowalność.
 *
 * Cena lustra jest jedna i nazwana: może się rozjechać ze słownikiem. Pilnuje
 * tego `tests/unit/supabase/uom300KitchenUnits.test.ts`, który porównuje ten plik
 * z krotkami `VALUES` w migracjach 295 i 300 i wywala się przy pierwszej
 * rozbieżności — w PR, a nie na produkcji.
 */

export interface UomEntry {
  /** Klucz główny `public.uom.code`. */
  code: string;
  /** `uom.dimension_code` — wymiar wielkości fizycznej. */
  dimension: string;
  /** `uom.name_pl` — etykieta dla człowieka. */
  label: string;
  /** `uom.deprecated_in_favor_of` — kod zalecany w miejsce tego. */
  deprecatedInFavorOf?: string;
}

/**
 * Wiersze `public.uom` z `tenant_id IS NULL` (rdzeń globalny), stan po migracji
 * 300. Jednostki najemcy (kod z kropką, np. `ogyy.karton`) NIE są tu lustrzane:
 * są z definicji zmienne w czasie działania i rozstrzyga je baza.
 */
export const UOM_CORE: readonly UomEntry[] = Object.freeze([
  { code: 'pH', dimension: 'acidity', label: 'pH' },
  { code: 'deg', dimension: 'angle', label: 'Stopien' },
  { code: 'rad', dimension: 'angle', label: 'Radian' },
  { code: 'cm2', dimension: 'area', label: 'Centymetr kwadratowy' },
  { code: 'm2', dimension: 'area', label: 'Metr kwadratowy' },
  { code: 'mm2', dimension: 'area', label: 'Milimetr kwadratowy' },
  { code: 'dozen', dimension: 'count', label: 'Tuzin' },
  { code: 'pcs', dimension: 'count', label: 'Sztuka' },
  {
    code: 'dz',
    dimension: 'count',
    label: 'Tuzin (kod historyczny)',
    deprecatedInFavorOf: 'dozen',
  },
  {
    code: 'szt',
    dimension: 'count',
    label: 'Sztuka (kod historyczny)',
    deprecatedInFavorOf: 'pcs',
  },
  { code: 'sztuk', dimension: 'count', label: 'Sztuka (odmiana)', deprecatedInFavorOf: 'pcs' },
  { code: 'sztuka', dimension: 'count', label: 'Sztuka (odmiana)', deprecatedInFavorOf: 'pcs' },
  { code: 'A', dimension: 'current', label: 'Amper' },
  { code: 'mA', dimension: 'current', label: 'Miliamper' },
  { code: 'kWh', dimension: 'energy', label: 'Kilowatogodzina' },
  { code: 'Wh', dimension: 'energy', label: 'Watogodzina' },
  { code: 'L/min', dimension: 'flow', label: 'Litr na minute' },
  { code: 'm3/h', dimension: 'flow', label: 'Metr szescienny na godzine' },
  { code: 'Hz', dimension: 'frequency', label: 'Herc' },
  { code: 'rpm', dimension: 'frequency', label: 'Obroty na minute' },
  { code: 'lux', dimension: 'illuminance', label: 'Luks' },
  { code: 'cm', dimension: 'length', label: 'Centymetr' },
  { code: 'in', dimension: 'length', label: 'Cal' },
  { code: 'm', dimension: 'length', label: 'Metr' },
  { code: 'mm', dimension: 'length', label: 'Milimetr' },
  { code: 'um', dimension: 'length', label: 'Mikrometr' },
  { code: 'bool', dimension: 'logical', label: 'Stan logiczny' },
  { code: 'dag', dimension: 'mass', label: 'Dekagram' },
  { code: 'g', dimension: 'mass', label: 'Gram' },
  { code: 'kg', dimension: 'mass', label: 'Kilogram' },
  { code: 'lb', dimension: 'mass', label: 'Funt' },
  { code: 'mg', dimension: 'mass', label: 'Miligram' },
  { code: 'oz', dimension: 'mass', label: 'Uncja' },
  { code: 't', dimension: 'mass', label: 'Tona' },
  {
    code: 'dkg',
    dimension: 'mass',
    label: 'Dekagram (zapis potoczny)',
    deprecatedInFavorOf: 'dag',
  },
  { code: 'opak', dimension: 'packaging', label: 'Opakowanie' },
  {
    code: 'opakowanie',
    dimension: 'packaging',
    label: 'Opakowanie (nazwa slowna)',
    deprecatedInFavorOf: 'opak',
  },
  { code: 'kW', dimension: 'power', label: 'Kilowat' },
  { code: 'W', dimension: 'power', label: 'Wat' },
  { code: 'atm', dimension: 'pressure', label: 'Atmosfera' },
  { code: 'bar', dimension: 'pressure', label: 'Bar' },
  { code: 'hPa', dimension: 'pressure', label: 'Hektopaskal' },
  { code: 'kPa', dimension: 'pressure', label: 'Kilopaskal' },
  { code: 'Pa', dimension: 'pressure', label: 'Paskal' },
  { code: 'psi', dimension: 'pressure', label: 'PSI' },
  { code: '%', dimension: 'ratio', label: 'Procent' },
  { code: 'ppm', dimension: 'ratio', label: 'Czesc na milion' },
  { code: '%rH', dimension: 'ratio', label: 'Wilgotnosc wzgledna', deprecatedInFavorOf: '%' },
  {
    code: 'percent',
    dimension: 'ratio',
    label: 'Procent (kod historyczny)',
    deprecatedInFavorOf: '%',
  },
  { code: 'dBm', dimension: 'signal_level', label: 'Decybel-miliwat' },
  { code: 'dB', dimension: 'sound_level', label: 'Decybel' },
  { code: '°C', dimension: 'temperature', label: 'Stopien Celsjusza' },
  { code: '°F', dimension: 'temperature', label: 'Stopien Fahrenheita' },
  { code: 'K', dimension: 'temperature', label: 'Kelwin' },
  {
    code: 'C',
    dimension: 'temperature',
    label: 'Stopien Celsjusza (kod historyczny)',
    deprecatedInFavorOf: '°C',
  },
  {
    code: 'celsius',
    dimension: 'temperature',
    label: 'Stopien Celsjusza (kod historyczny)',
    deprecatedInFavorOf: '°C',
  },
  {
    code: 'stopni',
    dimension: 'temperature',
    label: 'Stopien Celsjusza (nazwa slowna)',
    deprecatedInFavorOf: '°C',
  },
  { code: 'd', dimension: 'time', label: 'Doba' },
  { code: 'h', dimension: 'time', label: 'Godzina' },
  { code: 'min', dimension: 'time', label: 'Minuta' },
  { code: 's', dimension: 'time', label: 'Sekunda' },
  { code: 'Nm', dimension: 'torque', label: 'Niutonometr' },
  { code: 'm/s', dimension: 'velocity', label: 'Metr na sekunde' },
  { code: 'mm/s', dimension: 'velocity', label: 'Milimetr na sekunde' },
  { code: 'mPas', dimension: 'viscosity', label: 'Milipaskalosekunda' },
  { code: 'mV', dimension: 'voltage', label: 'Miliwolt' },
  { code: 'V', dimension: 'voltage', label: 'Wolt' },
  { code: 'gal', dimension: 'volume', label: 'Galon' },
  { code: 'l', dimension: 'volume', label: 'Litr' },
  { code: 'lyzeczka', dimension: 'volume', label: 'Lyzeczka' },
  { code: 'lyzka', dimension: 'volume', label: 'Lyzka' },
  { code: 'm3', dimension: 'volume', label: 'Metr szescienny' },
  { code: 'ml', dimension: 'volume', label: 'Mililitr' },
  { code: 'szklanka', dimension: 'volume', label: 'Szklanka' },
  { code: 'litr', dimension: 'volume', label: 'Litr (nazwa slowna)', deprecatedInFavorOf: 'l' },
  {
    code: 'łyżeczka',
    dimension: 'volume',
    label: 'Lyzeczka (zapis polski)',
    deprecatedInFavorOf: 'lyzeczka',
  },
  {
    code: 'łyżka',
    dimension: 'volume',
    label: 'Lyzka (zapis polski)',
    deprecatedInFavorOf: 'lyzka',
  },
]);

const BY_CODE: ReadonlyMap<string, UomEntry> = new Map(UOM_CORE.map((e) => [e.code, e]));

/**
 * Kody zgrupowane po `lower(code)`. Dopasowanie bez względu na wielkość liter
 * jest dopuszczalne WYŁĄCZNIE tam, gdzie grupa ma dokładnie jeden element —
 * dokładnie ta sama reguła co w `uom_resolve()` (krok 3).
 */
const BY_LOWER: ReadonlyMap<string, string[]> = (() => {
  const map = new Map<string, string[]>();
  for (const entry of UOM_CORE) {
    const key = entry.code.toLowerCase();
    const bucket = map.get(key);
    if (bucket) bucket.push(entry.code);
    else map.set(key, [entry.code]);
  }
  return map;
})();

/** Wszystkie kody rdzenia — zbiór do szybkiego „czy taki kod w ogóle istnieje". */
export const UOM_CORE_CODES: ReadonlySet<string> = new Set(UOM_CORE.map((e) => e.code));

/** Limit skoków po `deprecatedInFavorOf`. Lustro limitu z `uom_resolve()`. */
const MAX_ALIAS_HOPS = 8;

/**
 * Sprowadza zapis jednostki podany przez człowieka do kodu z `public.uom`.
 *
 * ODPOWIEDNIK 1:1 funkcji SQL `public.uom_resolve()` (migracja 300). Kroki, w tej
 * kolejności — każdy ma powód, wszystkie są sprawdzone testem po obu stronach:
 *
 *  1. `trim` — spacja z kopiuj/wklej nie jest treścią.
 *  2. DOPASOWANIE DOSŁOWNE. Idzie pierwsze, żeby `'C'` (alias Celsjusza) trafiał
 *     w siebie i żeby kod najemcy z kropką nie stracił ogona w kroku 4.
 *  3. WIELKOŚĆ LITER — ale tylko przy dopasowaniu JEDNOZNACZNYM. `'KG' -> 'kg'`
 *     jest bezpieczne; przy dwóch kodach różniących się wyłącznie wielkością
 *     liter funkcja ODMAWIA zamiast zgadywać.
 *  4. KROPKA KOŃCZĄCA. `'szt.' -> 'szt' -> 'pcs'`. Obcinana TYLKO z końca:
 *     `'st.C'` zostaje bez zmian i kończy jako `null`, bo nie ma powodu zgadywać,
 *     że chodziło o stopnie Celsjusza. (Sam kod `'szt.'` jest w słowniku
 *     NIEMOŻLIWY: `chk_uom_namespace` zakazuje kropki w kodzie rdzenia.)
 *  5. PODĄŻANIE ZA `deprecatedInFavorOf` — do skutku, z limitem skoków. Cykl
 *     aliasów znaczy „słownik jest niespójny" i wtedy jedyną uczciwą odpowiedzią
 *     jest `null`.
 *
 * Zwraca `null`, gdy nie da się rozstrzygnąć. `null` NIE znaczy „użyj wartości
 * domyślnej" — znaczy „odmów zapisu i powiedz człowiekowi, która wartość jest
 * nieznana", bo kolumna docelowa ma klucz obcy do `uom(code)`.
 *
 * Jednostek NAJEMCY (kod z kropką) ta funkcja nie zna i zwróci dla nich `null`.
 * To świadome: lustro obejmuje wyłącznie rdzeń, a odmowa jest bezpieczniejszym
 * błędem niż przepuszczenie kodu, którego istnienia nikt tu nie sprawdził.
 */
export function resolveUomCode(input: unknown): string | null {
  if (typeof input !== 'string') return null;

  const raw = input.trim();
  if (raw === '') return null;

  // Formy kandydujące w kolejności malejącej wierności zapisowi operatora.
  // Druga forma to `rtrim(raw, '. ')` z SQL-a: kropki i spacje TYLKO z końca.
  // Bez flagi `u`: klasa jest czysto ASCII, a jeden z pakietów (module-provisio)
  // celuje w ES5, gdzie ta flaga jest błędem składni.
  const trimmedTail = raw.replace(/[. ]+$/, '');
  const forms = trimmedTail === raw ? [raw] : [raw, trimmedTail];

  let code: string | null = null;
  for (const form of forms) {
    if (form === '') continue;

    if (BY_CODE.has(form)) {
      code = form;
      break;
    }

    const sameLower = BY_LOWER.get(form.toLowerCase());
    if (sameLower && sameLower.length === 1) {
      code = sameLower[0];
      break;
    }
  }

  if (code === null) return null;

  let resolved: string = code;
  let hops = 0;
  for (;;) {
    const next: string | undefined = BY_CODE.get(resolved)?.deprecatedInFavorOf;
    if (next === undefined) return resolved;
    hops += 1;
    if (hops > MAX_ALIAS_HOPS) return null;
    resolved = next;
  }
}

/** Etykieta jednostki dla ekranu. Zwraca sam kod, gdy słownik go nie zna. */
export function uomLabel(code: string): string {
  return BY_CODE.get(code)?.label ?? code;
}

/**
 * Kolumny objęte kluczem obcym do `uom(code)`, które ta lista obejmuje.
 * Zmierzone na żywej bazie 2026-10-17 (17:09 UTC, przed migracją UD-23)
 * zapytaniem:
 *
 *   select conrelid::regclass::text, pg_get_constraintdef(oid)
 *     from pg_constraint where confrelid = 'public.uom'::regclass;
 *
 * Wynik: 24 więzy, po jednej kolumnie; bez samo-referencyjnego
 * `uom.deprecated_in_favor_of` (kolumna słownika, którą wypełnia migracja,
 * a nie ładunek z zewnątrz) — 23 kolumny, po UD-23 (`products_jednostka_fkey`)
 * 24. Lista niżej obejmuje 13 z nich: dziewięć z migracji 295, jedną z 296
 * (`process_parameter_defs.canonical_uom_code`), dwie z samego słownika
 * (`uom_item_factors.from_uom_code` / `to_uom_code`) i jedną z UD-23
 * (`products.jednostka`). NIE obejmuje 11 kolumn z migracji 304, 307, 308,
 * 310, 385, 387, 398 i 401: `equipment.capacity_uom_code`,
 * `fabrica_characteristics.uom_code`, `fabrica_defect_observations.size_uom_code`,
 * `fabrica_instrument_models.measures_uom_code`,
 * `fabrica_instruments.measures_uom_code`,
 * `fabrica_production_substitutions.uom_code`,
 * `fabrica_production_tasks.spec_uom_code`,
 * `fabrica_quality_results.canonical_uom_code`,
 * `fabrica_recipe_tasks.spec_uom_code`,
 * `fabrica_substitution_compensations.adjust_uom_code`,
 * `fabrica_weighing_tolerances.tolerance_uom_code` — dla nich NEXUS nie
 * tłumaczy jednostki i oddaje surowe 23503 (wpis rejestru długu
 * `nexus_uom_kolumny_bez_tlumaczenia`, z terminem).
 *
 * Ta lista istnieje po to, żeby generyczna powierzchnia zapisu NEXUS-a
 * (`services/nexus-core/src/tools/SystemOperations.ts`) sprawdzała WARTOŚĆ
 * w kolumnach z listy, a nie tylko nazwę kolumny — do tej zmiany model
 * językowy mógł wpisać w nie dowolny łańcuch i dostać w odpowiedzi surowe
 * 23503.
 */
export const UOM_FK_COLUMNS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  fabrica_recipes: ['yield_unit'],
  fabrica_recipe_ingredients: ['unit'],
  fabrica_production_materials: ['unit'],
  fabrica_quality_results: ['unit'],
  fabrica_demand_forecasts: ['unit'],
  fabrica_schedule_entries: ['unit'],
  haccp_ccp_records: ['unit'],
  waterandice_measurements: ['unit'],
  waterandice_batches: ['unit'],
  process_parameter_defs: ['canonical_uom_code'],
  uom_item_factors: ['from_uom_code', 'to_uom_code'],
  // UD-23 (migracja `*_atrybuty_wspolne_produktu`, `products_jednostka_fkey`): jednostka karty produktu.
  // Kod wycofany (`szt`) baza odrzuca z nazwą kanonicznego (`pcs`) — NEXUS tłumaczy go przed zapisem.
  products: ['jednostka'],
});

/** Czy `column` w `table` jest objęta kluczem obcym do `uom(code)`. */
export function isUomColumn(table: string, column: string): boolean {
  return UOM_FK_COLUMNS[table]?.includes(column) ?? false;
}

// =============================================================================
// LISTY DO WYBORU NA EKRANIE
//
// Ekrany modułu FABRICA miały do tej zmiany gołe `<input type="text">` na
// jednostkę (`RecipeEditor.tsx:416`, `quality/page.tsx:481`) albo listę
// rozwijaną NIEZGODNĄ ze słownikiem (`recipes/page.tsx:10` oferowało `'opak'`,
// którego w `uom` nie było). Listy poniżej są jedynym źródłem tych opcji, a test
// `uomPickers` pilnuje, że każda pozycja jest kodem, który `resolveUomCode()`
// odwzorowuje SAMA NA SIEBIE — czyli że ekran nie podsuwa aliasu ani kodu spoza
// słownika.
// =============================================================================

/**
 * Jednostka wydajności receptury. `'opak'` ZOSTAJE na liście, bo po migracji 300
 * jest legalnym kodem własnego wymiaru `packaging` — a receptura na paczkę
 * pieczywa tostowego jest normalną recepturą i to właśnie ta pozycja była
 * najczęściej klikaną drogą do błędu 23503.
 */
export const UOM_YIELD_CHOICES: readonly string[] = Object.freeze([
  'pcs',
  'dozen',
  'opak',
  'kg',
  'g',
  'l',
  'ml',
]);

/**
 * Jednostka składnika w recepturze. Poza wagą i objętością SI zawiera miary
 * kuchenne z migracji 300: piekarz podaje sól w łyżeczkach, a nie w mililitrach.
 */
export const UOM_INGREDIENT_CHOICES: readonly string[] = Object.freeze([
  'kg',
  'g',
  'dag',
  'mg',
  'l',
  'ml',
  'szklanka',
  'lyzka',
  'lyzeczka',
  'pcs',
  'opak',
]);

/**
 * Jednostka wyniku kontroli jakości. `fabrica_quality_results.unit` jest
 * `NOT NULL` BEZ wartości domyślnej (zmierzone w `information_schema.columns`),
 * więc pole na ekranie musi mieć zawsze wybraną wartość — pusty wybór kończy się
 * błędem 23502, zanim klucz obcy zdąży cokolwiek powiedzieć.
 */
export const UOM_QUALITY_CHOICES: readonly string[] = Object.freeze([
  '°C',
  'g',
  'kg',
  'ml',
  'l',
  'mm',
  'cm',
  '%',
  'pH',
  'min',
  's',
  'pcs',
  'bar',
]);
